import { ArrowUpRight, X } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/cn.js';

export interface InspectorProps {
  title: string;
  /** Makes the title a link-styled button (for example "open this record in the provider"). */
  onTitleClick?: () => void;
  /** Tooltip and accessible name of the title button, e.g. the URL it opens. */
  titleActionLabel?: string;
  /** Mono subtitle (record id, provider). Case preserved. */
  subtitle?: string;
  /** Content directly below the title, kept visible above the scrolling sections. */
  titleDetails?: ReactNode;
  onClose?: () => void;
  /** Header actions (IconButtons, DropdownMenu). */
  actions?: ReactNode;
  children: ReactNode;
  /** Accessible region name. Default `Inspector`. */
  label?: string;
  className?: string;
}

export const INSPECTOR_MIN_WIDTH = 320;
export const INSPECTOR_MAX_WIDTH = 480;
export const INSPECTOR_DEFAULT_WIDTH = 360;

export function clampInspectorWidth(width: number): number {
  return Math.round(Math.min(INSPECTOR_MAX_WIDTH, Math.max(INSPECTOR_MIN_WIDTH, width)));
}

/** Right pane: header (title, mono subtitle, close) + scrolling stack of InspectorSections. */
export function Inspector({ title, onTitleClick, titleActionLabel, subtitle, titleDetails, onClose, actions, children, label = 'Inspector', className }: InspectorProps) {
  return (
    <aside aria-label={label} className={cn('flex h-full min-w-0 flex-col border-l border-line bg-panel', className)}>
      <header className="flex shrink-0 items-start justify-between gap-3 border-b border-hair px-4 py-3">
        <div className="min-w-0">
          <h2 className="truncate text-nav font-medium tracking-tight text-ink">
            {onTitleClick ? (
              <button
                type="button"
                onClick={onTitleClick}
                title={titleActionLabel}
                aria-label={titleActionLabel ? `${title} (${titleActionLabel})` : title}
                className="group inline-flex max-w-full items-center gap-1 rounded-sm text-left underline decoration-line underline-offset-[3px] transition-colors hover:text-info hover:decoration-info"
              >
                <span className="truncate">{title}</span>
                <ArrowUpRight size={12} aria-hidden className="shrink-0 text-mute transition-colors group-hover:text-info" />
              </button>
            ) : (
              title
            )}
          </h2>
          {titleDetails}
          {subtitle ? <p className="mt-0.5 truncate font-mono text-2xs text-mute">{subtitle}</p> : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {actions}
          {onClose ? (
            <button type="button" aria-label="Close inspector" onClick={onClose} className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-mute transition-colors hover:bg-card hover:text-ink">
              <X size={12} aria-hidden />
            </button>
          ) : null}
        </div>
      </header>
      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">{children}</div>
    </aside>
  );
}

export interface InspectorSectionProps {
  /** Mono uppercase section title. */
  title: string;
  /** Right side of the section header (count, ghost link). */
  rightSlot?: ReactNode;
  children: ReactNode;
  /** Collapsible with a disclosure. Default false. */
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Remove body padding for tables. */
  flush?: boolean;
  className?: string;
}

export function InspectorSection({ title, rightSlot, children, collapsible = false, defaultOpen = true, flush = false, className }: InspectorSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <section className={cn('border-b border-hair last:border-b-0', className)} aria-labelledby={`${id}-h`}>
      <div className="flex items-center justify-between gap-3 px-4 pt-3 pb-2">
        {collapsible ? (
          <button type="button" id={`${id}-h`} aria-expanded={open} aria-controls={`${id}-b`} onClick={() => setOpen((o) => !o)} className="label-mono flex items-center gap-1.5 rounded-sm text-left hover:text-ink">
            <ChevronDown size={10} aria-hidden className={cn('transition-transform motion-reduce:transition-none', open ? '' : '-rotate-90')} />
            {title}
          </button>
        ) : (
          <h3 id={`${id}-h`} className="label-mono">
            {title}
          </h3>
        )}
        {rightSlot ? <div className="flex items-center gap-2">{rightSlot}</div> : null}
      </div>
      {open ? (
        <div id={`${id}-b`} className={cn(flush ? 'pb-2' : 'px-4 pb-4', 'text-nav leading-snug text-ink/85')}>
          {children}
        </div>
      ) : null}
    </section>
  );
}
